import {memoize, uniq} from "lodash";
import React, {Suspense, useContext, useEffect, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {ParamTypes} from "../../constans/paramTypes";
import DashboardParamsContext from "../../context/DashboardParamsContext";
import useFilterValueGroups from "../../hooks/useFilterValueGroups";
import useQueryCallerResource from "../../hooks/useQueryCallerResource";
import {useSQLChartFormatters} from "../../hooks/useSQLChartFormatters";
import {getFarmID} from "../../selectors/farmSelector";
import {colors, colorsNonInvasive} from "../../utils/ColorUtils";
import {
    getParameterFormatter,
    getXAxisFormatterType,
    translateGroupName,
    translateGroupNames,
} from "../../utils/DashboardUtils";
import {createQueryForCustomComponent} from "../../utils/SQLUtils";
import Placeholder from "../basics/placeholder/Placeholder";
import SkeletonAreaChart from "../skeletons/SkeletonAreaChart";
import SkeletonBarChart from "../skeletons/SkeletonBarChart";
import SkeletonPieChartSolidWithLegend from "../skeletons/SkeletonPieChartSolidWithLegend";
import SkeletonRadarChart from "../skeletons/SkeletonRadarChart";
import SkeletonScatterChart from "../skeletons/SkeletonScatterChart";
import SQLChart from "./SQLChart";

const getMinMax = memoize(
    (data, parameters, maxYDomain) => {
        let min = Infinity;
        let max = -Infinity;
        for (let row of data) {
            for (let key of parameters) {
                if (row[key] < min) min = row[key];
                if (row[key] > max) max = row[key];
            }
        }
        if (min === Infinity || max === Infinity) return [0, "auto"];
        min = min < 0 ? min - 1 : 0;
        if (maxYDomain) return [min, +maxYDomain];
        return [min, Math.ceil(max * 1.05)];
    },
    (data, parameters, maxYDomain) =>
        JSON.stringify({data, parameters, maxYDomain})
);

function SQLChartWrapperRenderer({
    Parameters,
    Options,
    ReferenceLines,
    group,
    type,
    keysDictionary,
    onClick,
    highlight,
    canCreateDomain,
    print,
    filterValue,
    filterType,
    resource,
}) {
    const {t} = useTranslation();

    const data = resource.read();

    const farm = useSelector(getFarmID);

    const cachedDomain = useRef(null);

    const getFormatter = useSQLChartFormatters(type);

    const [leftAxisParameters, rightAxisParameters] = useMemo(() => {
        const left = [];
        const right = [];
        for (let param of Parameters) {
            if (!param.show) continue;
            if (param.axis === "left") left.push(param.id);
            if (param.axis === "right") right.push(param.id);
        }
        return [left, right];
    }, [Parameters]);

    const label = useMemo(() => translateGroupName(group, t), [group, t]);

    const formatters = useMemo(() => {
        const tmp = {};
        if (type === "Table") {
            for (let row of Parameters) {
                const type = getParameterFormatter(row, keysDictionary);
                tmp[row.id] = {
                    formatter: getFormatter(type, {precision: row.precision}),
                    type,
                };
            }
        } else {
            for (let row of Parameters) {
                if (!row.show) continue;
                const type = getParameterFormatter(row, keysDictionary);
                const formatter = getFormatter(type, {
                    precision: row.precision,
                });
                tmp[row.id] = {formatter, type};
                if (row.axis === "left") {
                    if (!tmp.y1) {
                        tmp.y1 = {formatter, type, parameters: []};
                    }
                    tmp.y1.parameters.push(row.id);
                }
                if (row.axis === "right") {
                    if (!tmp.y2) {
                        tmp.y2 = {formatter, type, parameters: []};
                    }
                    tmp.y2.parameters.push(row.id);
                }
            }
        }
        const xAxisType = getXAxisFormatterType(
            group,
            keysDictionary,
            Parameters[0]?.parameter
        );
        const options = {precision: Parameters[0]?.precision};
        if (xAxisType === ParamTypes.TIME) {
            options.dateFormat =
                Options.TimePeriod === "week"
                    ? `[${t("week")}] w`
                    : Options.TimePeriod === "month"
                      ? "MMMM"
                      : Options.TimePeriod === "year"
                        ? "YYYY"
                        : "L";
            options.useUTC = !!Options.TimePeriod;
        }
        if (xAxisType) {
            tmp.x = {
                formatter: getFormatter(xAxisType, options),
                type: xAxisType,
            };
        }
        return tmp;
    }, [
        Parameters,
        type,
        keysDictionary,
        group,
        getFormatter,
        Options.TimePeriod,
        t,
    ]);

    const _data = useMemo(() => {
        // trzeba dopisac recznie klucze, bo recharts nie radzi sobie z obiektami, ktore nie maja wszystkich kluczy
        if (!Options.InsertDomain) return data;
        const possibleKeys = data.reduce((prev, curr) => {
            const keys = Object.keys(curr);
            for (let key of keys) {
                prev.add(key);
            }
            return prev;
        }, new Set());
        return data.map((row) => {
            const tmp = {};
            for (let key of possibleKeys) {
                tmp[key] = row[key];
            }
            return tmp;
        });
    }, [data, Options.InsertDomain]);

    useEffect(() => {
        cachedDomain.current = null;
    }, [Options.YAxisDomainLeft]);

    const domains = useMemo(() => {
        if (!canCreateDomain) return cachedDomain.current;
        const leftDomain = getMinMax(
            _data,
            leftAxisParameters,
            Options.YAxisDomainLeft ? +Options.YAxisDomainLeft : null
        );
        const rightDomain = getMinMax(
            _data,
            rightAxisParameters,
            Options.YAxisDomainRight ? +Options.YAxisDomainRight : null
        );
        cachedDomain.current = [leftDomain, rightDomain];
        return [leftDomain, rightDomain];
    }, [
        canCreateDomain,
        _data,
        leftAxisParameters,
        rightAxisParameters,
        Options.YAxisDomainLeft,
        Options.YAxisDomainRight,
    ]);

    const groups = useFilterValueGroups(filterType, filterValue);

    const chartColors = useMemo(() => {
        const tmp = {};
        const defaultColors = uniq([
            ...Object.values(colorsNonInvasive),
            ...Object.values(colors),
        ]);
        let lastUsed = 0;
        for (let row of Parameters) {
            if (row.duplicateSeries) {
                if (!row.colors) {
                    for (let group of groups) {
                        tmp[`${row.id}#${group}`] =
                            defaultColors[lastUsed % defaultColors.length];
                        lastUsed++;
                    }
                } else {
                    for (let i = 0; i < groups.length; i++) {
                        tmp[`${row.id}#${groups[i]}`] =
                            row.colors[i % row.colors.length];
                    }
                }
            } else {
                if (type === "Pie") {
                    tmp[row.id] = defaultColors;
                } else if (!row.color) {
                    tmp[row.id] =
                        defaultColors[lastUsed % defaultColors.length];
                    lastUsed++;
                } else {
                    tmp[row.id] = row.color;
                }
            }
        }
        return tmp;
    }, [Parameters, groups, type]);

    const translatedGroupNames = useMemo(
        () => translateGroupNames(filterType, groups, farm),
        [filterType, groups, farm]
    );

    const frozenHeaders = useMemo(() => {
        if (!type === "Table") return [];
        let tmp = Parameters.filter((item) => item.freeze).map(
            (item) => item.id
        );
        for (let key in Options.Animal || {}) {
            if (Options.Animal[key]) tmp.push(key);
        }
        return tmp;
    }, [Parameters, type, Options.Animal]);

    const translatedIDs = useMemo(() => {
        const tmp = {};
        for (let row of Parameters) {
            if (row.show) {
                if (row.duplicateSeries) {
                    for (let group of groups) {
                        tmp[`${row.id}#${group}`] = row.parameterName;
                    }
                } else {
                    tmp[row.id] = row.parameterName;
                }
            }
        }
        return tmp;
    }, [Parameters, groups]);

    const summaryFields = useMemo(() => {
        if (type !== "Table") return [];
        return Parameters.filter(
            (item) => item.summary && item.summary !== "none"
        ).map((item) => ({id: item.id, type: item.summary}));
    }, [Parameters, type]);

    const legendCalculation = useMemo(() => {
        if (type === "Table") return null;
        const tmp = {};
        for (let row of Parameters) {
            if (row.duplicateSeries) {
                for (let group of groups) {
                    tmp[`${row.id}#${group}`] = row.legendCalculation;
                }
            } else {
                tmp[row.id] = row.legendCalculation;
            }
        }
        return tmp;
    }, [Parameters, type, groups]);

    const showOnlyLabelsWithValue = useMemo(() => {
        switch (filterType) {
            case "group":
            case "technologyGroup":
                return group === "group_nr";
            case "cycle":
                return group === "cycle";
            default:
                return false;
        }
    }, [group, filterType]);

    return (
        <div className="h-100">
            <SQLChart
                data={_data}
                chartType={type}
                label={label}
                referenceLines={ReferenceLines}
                secondAxisParameters={rightAxisParameters}
                formatters={formatters}
                onClick={onClick}
                highlight={highlight}
                stack={Options.Stack}
                legend={Options.Legend}
                dataTransposition={!!Options.DataTransposition}
                showAxisName={Options.ShowAxisName}
                leftDomain={domains?.[0]}
                rightDomain={domains?.[1]}
                print={print}
                chartColors={chartColors}
                translatedGroupNames={translatedGroupNames}
                frozenHeaders={frozenHeaders}
                translatedIDs={translatedIDs}
                showIndex={Options.ShowIndex}
                summaryFields={summaryFields}
                legendCalculation={legendCalculation}
                labelList={Options.LabelList}
                showOnlyLabelsWithValue={showOnlyLabelsWithValue}
            />
        </div>
    );
}

function SQLChartWrapperSkeleton({type}) {
    if (type === "Pie") return <SkeletonPieChartSolidWithLegend />;
    if (type === "Bar") return <SkeletonBarChart />;
    if (type === "Table") return <Placeholder type="table" />;
    if (type === "Scatter") return <SkeletonScatterChart />;
    if (type === "Radar") return <SkeletonRadarChart />;
    return <SkeletonAreaChart />;
}

function SQLChartWrapperSimple({
    Parameters,
    Options,
    ReferenceLines,
    group,
    type,
    keysDictionary,
    onClick,
    highlight,
    query,
    canCreateDomain,
    print,
    filterValue,
    filterType,
}) {
    const resource = useQueryCallerResource(query);

    return (
        <div className="h-100 w-100">
            {Parameters.length > 0 && (
                <Suspense
                    fallback={
                        <div className="w-100 h-100" data-wait="true">
                            <SQLChartWrapperSkeleton type={type} />
                        </div>
                    }>
                    <SQLChartWrapperRenderer
                        Options={Options}
                        Parameters={Parameters}
                        ReferenceLines={ReferenceLines}
                        group={group}
                        type={type}
                        keysDictionary={keysDictionary}
                        onClick={onClick}
                        highlight={highlight}
                        canCreateDomain={canCreateDomain}
                        print={print}
                        filterValue={filterValue}
                        filterType={filterType}
                        resource={resource}
                    />
                </Suspense>
            )}
        </div>
    );
}

export default function SQLChartWrapper({
    Parameters,
    Filter,
    Options,
    ReferenceLines,
    group,
    type,
    keysDictionary,
    onClick,
    filterItem,
    highlight,
    filterValue,
    print,
    filterType,
    limit,
}) {
    const [params, loading] = useContext(DashboardParamsContext);

    const query = useMemo(() => {
        if (Parameters.length === 0) return null;
        return createQueryForCustomComponent(
            Parameters,
            group,
            Filter,
            Options,
            filterItem,
            filterValue,
            params,
            limit
        );
    }, [
        Parameters,
        group,
        Filter,
        Options,
        filterItem,
        filterValue,
        params,
        limit,
    ]);

    if (loading)
        return (
            <div className="w-100 h-100" data-wait="true">
                <SQLChartWrapperSkeleton type={type} />
            </div>
        );
    return (
        <SQLChartWrapperSimple
            Options={Options}
            Parameters={Parameters}
            ReferenceLines={ReferenceLines}
            group={group}
            type={type}
            keysDictionary={keysDictionary}
            onClick={onClick}
            highlight={highlight}
            query={query}
            canCreateDomain={!filterItem || filterItem.length === 0}
            print={print}
            filterValue={filterValue}
            filterType={filterType}
        />
    );
}
